import i18n, { currentLocale } from "@/i18n";
import { getStudioTimezone, zonedParts } from "@/lib/timezone";

/**
 * Date / time formatters that match the design system's voice:
 * mono HH:MM for times, sentence-case labels, 12-hour is never used
 * (the design system mandates HH:MM mono everywhere).
 *
 * Every instant is rendered in the **studio timezone** (issue #203), not
 * UTC: lesson times are stored as UTC but must read as the studio's local
 * wall-clock time. Date, weekday, and number formatting follow the app's
 * active language via `currentLocale()`; the mono HH:MM time is
 * locale-neutral by design.
 */

/** "06:30" — 24-hour HH:MM, in the studio timezone. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const { hour, minute } = zonedParts(d, getStudioTimezone());
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** "Tuesday, June 23" — weekday + month + day, sentence case. */
export function formatLongDate(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLocale(), {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: getStudioTimezone(),
  });
}

/** "Jun 23" — abbreviated month + day, for compact pills on small screens. */
export function formatShortDate(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLocale(), {
    month: "short",
    day: "numeric",
    timeZone: getStudioTimezone(),
  });
}

/** "June 2026" — month + year, in the studio timezone (history headers). */
export function formatMonth(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLocale(), {
    month: "long",
    year: "numeric",
    timeZone: getStudioTimezone(),
  });
}

/** "01 January 2026" — zero-padded day + full month + year, studio timezone. */
export function formatFullDate(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  const { day } = zonedParts(date, getStudioTimezone());
  return `${String(day).padStart(2, "0")} ${formatMonth(date)}`;
}

/** "23" — just the day-of-month, used in the day picker. */
export function formatDay(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return String(zonedParts(date, getStudioTimezone()).day);
}

/** "Mon" — 3-letter weekday, used in the day picker. */
export function formatWeekday(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLocale(), {
    weekday: "short",
    timeZone: getStudioTimezone(),
  });
}

/** "45 min" / "1h 30m" — duration from minutes, localized. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return i18n.t("format.durationMin", { count: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0
    ? i18n.t("format.durationH", { hours: h })
    : i18n.t("format.durationHM", { hours: h, minutes: m });
}

/** "2 days" / "1 day" — whole days, localized (issue #37 open window). */
export function formatDays(days: number): string {
  return i18n.t("format.days", { count: days });
}

/** "06:30 · 45 min" — combined, for the class detail meta grid. */
export function formatTimeAndDuration(iso: string, minutes: number): string {
  return `${formatTime(iso)} · ${formatDuration(minutes)}`;
}

/** "1,240" — number with thousands separators. */
export function formatNumber(n: number | string): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString(currentLocale());
}

/** "Sunrise Flow" → "sunrise-flow" — slug for navigation keys. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
